import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { SiteFooter } from "@/components/SiteNav";
import { TAX_ANSWERS_BY_SLUG, KIND_LABEL } from "@/lib/tax-answers";

const BASE = "https://mytaxfella.com/tax-answers";

export const Route = createFileRoute("/tax-answers/$slug")({
  loader: ({ params }) => {
    if (params.slug === "cpa-vs-ea-vs-tax-preparer") {
      throw redirect({ to: "/tax-answers/$slug", params: { slug: "how-much-does-tax-prep-cost-2026" }, statusCode: 301 });
    }
    const answer = TAX_ANSWERS_BY_SLUG[params.slug];
    if (!answer) throw notFound();
    return { answer };
  },
  head: ({ params, loaderData }) => {
    const a = loaderData?.answer;
    if (!a) return { meta: [{ title: "Answer Not Found | MyTaxFella" }, { name: "robots", content: "noindex" }] };
    const url = `${BASE}/${params.slug}`;
    return {
      meta: [
        { title: a.title },
        { name: "description", content: a.description },
        { property: "og:title", content: a.title }, { property: "og:image", content: "https://mytaxfella.com/og-share-2026.jpg" }, { name: "twitter:image", content: "https://mytaxfella.com/og-share-2026.jpg" },
        { property: "og:description", content: a.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: a.h1,
            description: a.description,
            dateModified: a.lastmod,
            mainEntityOfPage: url,
            author: { "@id": "https://mytaxfella.com/#organization" },
            publisher: { "@id": "https://mytaxfella.com/#organization" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://mytaxfella.com/" },
              { "@type": "ListItem", position: 2, name: "Tax Answers", item: BASE },
              { "@type": "ListItem", position: 3, name: a.label, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: AnswerPage,
  notFoundComponent: () => (
    <main className="section-y text-center px-6">
      <h1 className="section-h2">Answer not found</h1>
      <Link to="/tax-answers" className="btn-green mt-6">Browse Tax Answers →</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="section-y text-center px-6">
      <h1 className="section-h2">Something went wrong</h1>
      <Link to="/tax-answers" className="btn-green mt-6">Browse Tax Answers →</Link>
    </main>
  ),
});

function AnswerPage() {
  const { answer: a } = Route.useLoaderData();
  return (
    <main>
      <article className="section-y">
        <div className="max-w-[820px] mx-auto px-6 lg:px-10">
          <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
            <Link to="/tax-answers" className="hover:text-green">Tax Answers</Link>
            <span aria-hidden="true"> / </span>
            <span>{KIND_LABEL[a.kind]}</span>
          </nav>
          <h1 className="section-h2 mt-4">{a.h1}</h1>
          <p className="mt-6 text-lg leading-relaxed text-foreground">{a.summary}</p>

          {a.deadline && (
            <div className="pc-card mt-8 border-green">
              <h2 className="font-semibold text-foreground">Deadline</h2>
              <p className="mt-2 text-muted-foreground">{a.deadline}</p>
            </div>
          )}

          {a.sections.map((s) => (
            <section key={s.h2} className="mt-10">
              <h2 className="text-2xl font-semibold text-foreground">{s.h2}</h2>
              {s.body.map((p) => (
                <p key={p} className="mt-3 text-muted-foreground leading-relaxed">{p}</p>
              ))}
              {s.list && (
                <ul className="mt-4 list-disc pl-5 space-y-2 text-muted-foreground">
                  {s.list.map((li) => <li key={li}>{li}</li>)}
                </ul>
              )}
            </section>
          ))}

          <section className="mt-10">
            <h2 className="text-2xl font-semibold text-foreground">Common questions</h2>
            <dl className="mt-4 space-y-5">
              {a.faqs.map((f) => (
                <div key={f.q}>
                  <dt className="font-semibold text-foreground">{f.q}</dt>
                  <dd className="mt-1 text-muted-foreground leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </section>

          <div className="pc-card mt-12 text-center">
            <h2 className="text-xl font-semibold text-foreground">Want someone to handle it for you?</h2>
            <p className="mt-2 text-muted-foreground">
              Our Enrolled Agents review your situation and tell you the next step in plain language.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link to={a.cta.to} className="btn-green">{a.cta.label} →</Link>
              <a href="tel:+17183565178" className="btn-ghost">(718) 356-5178</a>
            </div>
          </div>

          <section className="mt-12">
            <h2 className="text-xl font-semibold text-foreground">Related answers</h2>
            <ul className="mt-4 grid sm:grid-cols-3 gap-3">
              {a.related.map((slug) => {
                const r = TAX_ANSWERS_BY_SLUG[slug];
                if (!r) return null;
                return (
                  <li key={slug}>
                    <Link to="/tax-answers/$slug" params={{ slug }} className="pc-card block h-full text-sm font-semibold hover:border-green">
                      {r.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>

          <p className="mt-10 text-xs text-muted-foreground">
            General information, not tax advice for your specific situation. Last reviewed {a.lastmod}.
          </p>
        </div>
      </article>
      <SiteFooter />
    </main>
  );
}
